import { API_ENDPOINTS } from '../config/api';

// Calendar Service Class for browser-compatible API management
class CalendarService {

    // Base API URL - Uses environment variable or defaults to localhost
    baseUrl = API_ENDPOINTS.CALENDAR;

    // Helper method for API calls
    async apiCall(endpoint, options = {}) {
        try {
            const url = `${this.baseUrl}${endpoint}`;
            console.log(`Making API call to: ${url}`);
            console.log('Request options:', options);

            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers
                },
                mode: 'cors',
                credentials: 'omit',
                ...options
            });

            console.log(`Response status: ${response.status} ${response.statusText}`);
            console.log('Response headers:', response.headers);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response body:', errorText);
                throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const data = await response.json();
            console.log('Response data:', data);
            return data;
        } catch (error) {
            console.error(`API call error for ${endpoint}:`, error);
            console.error('Error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });

            // Provide more specific error messages
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                throw new Error(`Network error: Unable to reach ${this.baseUrl}${endpoint}. Please check if the API server is running and accessible.`);
            }

            throw error;
        }
    }

    // Create a new calendar event
    async createEvent(eventData) {
        try {
            console.log('Creating calendar event via API:', eventData);

            const response = await this.apiCall('', {
                method: 'POST',
                body: JSON.stringify(eventData)
            });

            console.log('Event created successfully:', response);
            return response;
        } catch (error) {
            console.error('Error creating calendar event:', error);
            throw error;
        }
    }

    // Get all calendar events
    async getAllEvents() {
        try {
            const response = await this.apiCall('');
            console.log('All events retrieved:', response);
            return response || [];
        } catch (error) {
            console.error('Error fetching all events:', error);
            throw error;
        }
    }

    // Get event by ID
    async getEventById(eventId) {
        try {
            // Since there's no direct get by ID endpoint, we'll get all and filter
            const allEvents = await this.getAllEvents();
            const event = allEvents.find(e => e.id === eventId);

            if (!event) {
                throw new Error('Event not found');
            }

            return event;
        } catch (error) {
            console.error('Error fetching event by ID:', error);
            throw error;
        }
    }

    // Update event by ID
    async updateEvent(eventId, updateData) {
        try {
            console.log('Updating calendar event via API:', { id: eventId, ...updateData });

            const response = await this.apiCall(`/${eventId}`, {
                method: 'PUT',
                body: JSON.stringify(updateData)
            });

            console.log('Event updated successfully:', response);
            return response;
        } catch (error) {
            console.error('Error updating calendar event:', error);
            throw error;
        }
    }

    // Delete event by ID
    async deleteEvent(eventId) {
        try {
            console.log('Deleting calendar event via API:', eventId);

            const response = await this.apiCall(`/${eventId}`, {
                method: 'DELETE'
            });

            console.log('Event deleted successfully:', response);
            return response;
        } catch (error) {
            console.error('Error deleting calendar event:', error);
            throw error;
        }
    }

    // Get events by date range
    async getEventsByDateRange(startDate, endDate) {
        try {
            const allEvents = await this.getAllEvents();
            const filteredEvents = allEvents.filter(event => {
                if (!event.StartTime || !event.EndTime) return false;
                const start = new Date(event.StartTime);
                const end = new Date(event.EndTime);
                return start >= startDate && end <= endDate;
            });

            return filteredEvents.sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));
        } catch (error) {
            console.error('Error fetching events by date range:', error);
            throw error;
        }
    }

    // Get all appointments
    async getAppointments() {
        try {
            const response = await this.apiCall('/appointments');
            console.log('Appointments retrieved:', response);
            return response || [];
        } catch (error) {
            console.error('Error fetching appointments:', error);
            throw error;
        }
    }

    // Get appointments by date range
    async getAppointmentsByDateRange(startDate, endDate) {
        try {
            const appointments = await this.getAppointments();
            const filteredAppointments = appointments.filter(event => {
                if (!event.StartTime || !event.EndTime) return false;
                const start = new Date(event.StartTime);
                const end = new Date(event.EndTime);
                return start >= startDate && end <= endDate;
            });

            return filteredAppointments;
        } catch (error) {
            console.error('Error fetching appointments by date range:', error);
            throw error;
        }
    }

    // Get appointments by parent
    async getAppointmentsByParent(parent) {
        try {
            const appointments = await this.getAppointments();
            const filteredAppointments = appointments.filter(event => event.parent === parent);

            return filteredAppointments.sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));
        } catch (error) {
            console.error('Error fetching appointments by parent:', error);
            throw error;
        }
    }

    // Get all weekend duties
    async getWeekendDuties() {
        try {
            const response = await this.apiCall('/weekend-duties');
            console.log('Weekend duties retrieved:', response);
            return response || [];
        } catch (error) {
            console.error('Error fetching weekend duties:', error);
            throw error;
        }
    }

    // Get weekend duties by date range
    async getWeekendDutiesByDateRange(startDate, endDate) {
        try {
            const weekendDuties = await this.getWeekendDuties();
            const filteredDuties = weekendDuties.filter(event => {
                if (!event.weekendDate) return false;
                const dutyDate = new Date(event.weekendDate);
                return dutyDate >= startDate && dutyDate <= endDate;
            });

            return filteredDuties;
        } catch (error) {
            console.error('Error fetching weekend duties by date range:', error);
            throw error;
        }
    }

    // Get events by family ID
    async getEventsByFamily(familyId) {
        try {
            const allEvents = await this.getAllEvents();
            const familyEvents = allEvents.filter(event => {
                if (typeof event.FamilyId === 'object' && event.FamilyId.id) {
                    return event.FamilyId.id === familyId;
                }
                return event.FamilyId === familyId;
            });

            return familyEvents.sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));
        } catch (error) {
            console.error('Error fetching events by family:', error);
            throw error;
        }
    }

    // Get upcoming events (next N days)
    async getUpcomingEvents(days = 7) {
        try {
            const startDate = new Date();
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + days);

            return await this.getEventsByDateRange(startDate, endDate);
        } catch (error) {
            console.error('Error fetching upcoming events:', error);
            throw error;
        }
    }

    // Get overdue events
    async getOverdueEvents() {
        try {
            const now = new Date();
            const allEvents = await this.getAllEvents();
            const overdueEvents = allEvents.filter(event => {
                if (!event.EndTime || event.Status === 'Completed') return false;
                const endTime = new Date(event.EndTime);
                return endTime < now;
            });

            return overdueEvents.sort((a, b) => new Date(a.EndTime) - new Date(b.EndTime));
        } catch (error) {
            console.error('Error fetching overdue events:', error);
            throw error;
        }
    }

    // Search events by text
    async searchEvents(searchTerm) {
        try {
            const allEvents = await this.getAllEvents();
            const searchResults = allEvents.filter(event => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    (event.Subject && event.Subject.toLowerCase().includes(searchLower)) ||
                    (event.Description && event.Description.toLowerCase().includes(searchLower)) ||
                    (event.Location && event.Location.toLowerCase().includes(searchLower))
                );
            });

            return searchResults.sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));
        } catch (error) {
            console.error('Error searching events:', error);
            throw error;
        }
    }

    // Get event statistics
    async getEventStats() {
        try {
            const response = await this.apiCall('/stats');
            console.log('Event statistics retrieved:', response);
            return response || {};
        } catch (error) {
            console.error('Error getting event statistics:', error);
            throw error;
        }
    }

    // Test API connection
    async testConnection() {
        try {
            console.log('Testing API connectivity...');

            // First, try a simple HEAD request to check if the server is reachable
            try {
                const headResponse = await fetch(`${this.baseUrl}`, {
                    method: 'HEAD',
                    mode: 'cors',
                    credentials: 'omit'
                });
                console.log('HEAD request successful:', headResponse.status);
            } catch (headError) {
                console.log('HEAD request failed, trying GET:', headError.message);
            }

            // Now try the actual API call
            const response = await this.apiCall('');
            console.log('API connection test successful:', response);
            return true;
        } catch (error) {
            console.error('API connection test failed:', error);
            return false;
        }
    }

    // Get API status
    async getApiStatus() {
        try {
            const response = await this.apiCall('/');
            return response;
        } catch (error) {
            console.error('Error getting API status:', error);
            throw error;
        }
    }

    // Simple ping test to check if domain is reachable
    async pingApi() {
        try {
            console.log('Pinging API domain...');
            const startTime = Date.now();

            const response = await fetch(`${this.baseUrl}`, {
                method: 'GET',
                mode: 'cors',
                credentials: 'omit',
                signal: AbortSignal.timeout(5000) // 5 second timeout
            });

            const endTime = Date.now();
            const latency = endTime - startTime;

            console.log(`API ping successful: ${response.status} (${latency}ms)`);
            return {
                reachable: true,
                status: response.status,
                latency: latency
            };
        } catch (error) {
            console.error('API ping failed:', error);
            return {
                reachable: false,
                error: error.message
            };
        }
    }
}

const calendarService = new CalendarService();
export default calendarService;
